import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  Flex,
  Text,
  Button,
  Select,
  Separator,
  IconButton,
} from "@radix-ui/themes";
import { ArrowRightIcon } from "@radix-ui/react-icons";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../context/ToastContext";
import { ToastAlert } from "components";
import { useProfileContext, useLocationContext } from "context";
import { useGetAllBrands, useSaveAtreemoMappings } from "hooks";

export function AtreemoConfigSetupPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setToastMessage } = useToast();
  const { userProfile } = useProfileContext();

  // Atreemo-provided info (from location.state)
  const [atreemoBrands, setAtreemoBrands] = useState([]);
  const [atreemoLocations, setAtreemoLocations] = useState([]);

  // bconnect data
  const { data: brandsData, isLoading: isLoadingBrands } = useGetAllBrands(userProfile?.organisation_id);
  const { locationsData } = useLocationContext();

  const [bconnectBrands, setBconnectBrands] = useState([]); // non-default bconnect brands
  const [independentLocations, setIndependentLocations] = useState([]); // bconnect locations for "Locations" section

  // single mapping state keyed by bconnect_location_id
  const [locationMappings, setLocationMappings] = useState({});
  const [originalLocationMappings, setOriginalLocationMappings] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const [progressValue, setProgressValue] = useState(0);

  const saveAtreemoMappings = useSaveAtreemoMappings();

  // Normalize Atreemo brands & locations from location.state (no association)
  useEffect(() => {
    const brandsFromState = location.state?.available_brands || [];
    setAtreemoBrands(
      brandsFromState.map((b) => ({
        id: String(b.BrandID),
        name: b.BrandName,
      }))
    );

    const locationsFromState = location.state?.available_locations || [];
    setAtreemoLocations(
      locationsFromState.map((l) => ({
        id: String(l.LocationID),
        name: l.LocationName,
      }))
    );
  }, [location.state]);

  // Prepare bconnect lists (mirror setup logic)
  useEffect(() => {
    if (brandsData && locationsData) {
      const defaultBrand = brandsData.find((b) => b.is_default);
      const nonDefault = brandsData.filter((b) => !b.is_default);
      setBconnectBrands(nonDefault || []);
      const defaultLocations = locationsData.filter((loc) => loc.brand_id === defaultBrand?.id || loc.brand_id === null);
      setIndependentLocations(defaultLocations || []);
    }
  }, [brandsData, locationsData]);

  // Build a map of brand -> its locations (prefer embedded brandsData[].locations)
  const brandLocationsMap = useMemo(() => {
    const map = {};
    if (!bconnectBrands) return map;

    bconnectBrands.forEach((b) => {
      const sourceBrand = (brandsData || []).find((bd) => String(bd.id) === String(b.id));
      if (sourceBrand && Array.isArray(sourceBrand.locations)) {
        map[b.id] = sourceBrand.locations;
      } else {
        map[b.id] = (locationsData || []).filter((loc) => String(loc.brand_id) === String(b.id));
      }
    });

    return map;
  }, [bconnectBrands, brandsData, locationsData]);

  // Also provide a flat list of all brand-owned locations
  const allBrandOwnedLocations = useMemo(() => Object.values(brandLocationsMap).flat(), [brandLocationsMap]);

  // If initial mappings available in location.state, use them. Otherwise start empty.
  useEffect(() => {
    const mappings = location.state?.mappings || null;
    if (!mappings) {
      setLocationMappings({});
      setOriginalLocationMappings({});
      return;
    }

    const lm = mappings.location_mappings || [];
    const locMap = {};
    const locOrig = {};

    lm.forEach((m) => {
      const key = String(m.bconnect_location_id);
      const entry = {
        atreemo_brand_id: m.atreemo_brand_id || "",
        atreemo_location_id: m.atreemo_location_id || "",
        // isMapped true if at least one of the fields is present
        isMapped: Boolean(m.atreemo_brand_id || m.atreemo_location_id),
      };
      locMap[key] = entry;
      locOrig[key] = { atreemo_brand_id: entry.atreemo_brand_id, atreemo_location_id: entry.atreemo_location_id };
    });

    setLocationMappings(locMap);
    setOriginalLocationMappings(locOrig);
  }, [location.state]);

  // Handlers for per-location mapping (centralized)
  const handleLocationAtreemoBrandChange = (bconnectLocationId, atreemoBrandId) => {
    const key = String(bconnectLocationId);
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      const existing = next[key] || { atreemo_brand_id: "", atreemo_location_id: "", isMapped: false };
      next[key] = { ...existing, atreemo_brand_id: atreemoBrandId, isMapped: Boolean(atreemoBrandId || existing.atreemo_location_id) };
      return next;
    });
  };
  const handleLocationAtreemoLocationChange = (bconnectLocationId, atreemoLocationId) => {
    const key = String(bconnectLocationId);
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      const existing = next[key] || { atreemo_brand_id: "", atreemo_location_id: "", isMapped: false };
      next[key] = { ...existing, atreemo_location_id: atreemoLocationId, isMapped: Boolean(existing.atreemo_brand_id || atreemoLocationId) };
      return next;
    });
  };

  // Validation & "dirty" detection for the brands (brand-owned locations) section
  const hasBrandSectionChanges = useMemo(() => {
    const brandLocations = allBrandOwnedLocations || [];

    // 1) If any row is half-filled -> invalid (disable save)
    for (const loc of brandLocations) {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const currBrand = String(curr.atreemo_brand_id || "");
      const currLoc = String(curr.atreemo_location_id || "");
      const isHalfFilled = (!!currBrand) !== (!!currLoc);
      if (isHalfFilled) return false;
    }

    // 2) Must have at least one fully-populated row that actually changed from original
    const hasAtLeastOneFullyPopulatedChange = brandLocations.some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const orig = originalLocationMappings[key] || { atreemo_brand_id: "", atreemo_location_id: "" };
      const currBrand = String(curr.atreemo_brand_id || "");
      const currLoc = String(curr.atreemo_location_id || "");
      const origBrand = String(orig.atreemo_brand_id || "");
      const origLoc = String(orig.atreemo_location_id || "");

      if (!currBrand || !currLoc) return false;
      return currBrand !== origBrand || currLoc !== origLoc;
    });

    return Boolean(hasAtLeastOneFullyPopulatedChange);
  }, [allBrandOwnedLocations, locationMappings, originalLocationMappings]);

  // Validation & "dirty" detection for the independent (default) locations section
  const hasIndependentLocationChanges = useMemo(() => {
    const indep = independentLocations || [];

    for (const loc of indep) {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const currBrand = String(curr.atreemo_brand_id || "");
      const currLoc = String(curr.atreemo_location_id || "");
      const isHalfFilled = (!!currBrand) !== (!!currLoc);
      if (isHalfFilled) return false;
    }

    const hasAtLeastOneFullyPopulatedChange = indep.some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const orig = originalLocationMappings[key] || { atreemo_brand_id: "", atreemo_location_id: "" };
      const currBrand = String(curr.atreemo_brand_id || "");
      const currLoc = String(curr.atreemo_location_id || "");
      const origBrand = String(orig.atreemo_brand_id || "");
      const origLoc = String(orig.atreemo_location_id || "");

      if (!currBrand || !currLoc) return false;
      return currBrand !== origBrand || currLoc !== origLoc;
    });

    return Boolean(hasAtLeastOneFullyPopulatedChange);
  }, [independentLocations, locationMappings, originalLocationMappings]);

  // true if any brand-owned location row is half-filled (one field present, the other missing)
  const hasBrandHalfFilled = useMemo(() => {
    const brandLocations = allBrandOwnedLocations || [];
    for (const loc of brandLocations) {
      const locId = loc.id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const currBrand = String(curr.atreemo_brand_id || "");
      const currLoc = String(curr.atreemo_location_id || "");
      if ((!!currBrand) !== (!!currLoc)) return true;
    }
    return false;
  }, [allBrandOwnedLocations, locationMappings]);

  // true if any independent/default-location row is half-filled
  const hasIndependentHalfFilled = useMemo(() => {
    const indep = independentLocations || [];
    for (const loc of indep) {
      const locId = loc.id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const currBrand = String(curr.atreemo_brand_id || "");
      const currLoc = String(curr.atreemo_location_id || "");
      if ((!!currBrand) !== (!!currLoc)) return true;
    }
    return false;
  }, [independentLocations, locationMappings]);

  // Save handler which can save brand-owned or independent or both sections
  const handleSaveLocationMappings = async (section) => {
    try {
      setIsLoading(true);
      setProgressValue(10);

      let selectedLocations = [];
      if (section === "brands") {
        selectedLocations = allBrandOwnedLocations || [];
      } else if (section === "independent") {
        selectedLocations = independentLocations || [];
      } else {
        selectedLocations = [
          ...(independentLocations || []),
          ...(allBrandOwnedLocations || []),
        ];
      }

      const locationPayload = (selectedLocations || [])
        .map((loc) => {
          const locId = loc.id ?? loc.value ?? loc.location_id;
          const key = String(locId);
          const curr = locationMappings[key] || {};
          const currBrand = curr.atreemo_brand_id || null;
          const currLoc = curr.atreemo_location_id || null;
          const orig = originalLocationMappings[key] || {};
          const origBrand = orig.atreemo_brand_id || null;
          const origLoc = orig.atreemo_location_id || null;

          // require both fields to be present to save
          if (!currBrand || !currLoc) return null;

          // skip unchanged rows
          if (currBrand === origBrand && currLoc === origLoc) return null;

          const atreemoBrandObj = atreemoBrands.find((ab) => String(ab.id) === String(currBrand));
          const atreemoLocationObj = atreemoLocations.find((al) => String(al.id) === String(currLoc));
          return {
            bconnect_location_id: locId,
            bconnect_location_name: loc.name ?? loc.label ?? "",
            atreemo_brand_id: currBrand,
            atreemo_brand_name: atreemoBrandObj?.name || "",
            atreemo_location_id: currLoc,
            atreemo_location_name: atreemoLocationObj?.name || "",
          };
        })
        .filter(Boolean);

      if (locationPayload.length === 0) {
        setToastMessage("No location changes to save");
        setIsLoading(false);
        setProgressValue(0);
        return;
      }

      setProgressValue(60);

      const payload = {
        organisation_id: userProfile.organisation_id,
        brand_mappings: [], // location-only save here
        location_mappings: locationPayload,
      };

      const result = await saveAtreemoMappings.mutateAsync(payload);
      setProgressValue(95);

      if (result?.data?.status !== "success") {
        throw new Error(result?.data?.message || "Failed to save location mappings");
      }

      // Update originals for only the saved rows
      const newOrig = { ...originalLocationMappings };
      locationPayload.forEach((p) => {
        newOrig[String(p.bconnect_location_id)] = {
          atreemo_brand_id: p.atreemo_brand_id,
          atreemo_location_id: p.atreemo_location_id,
        };
      });
      setOriginalLocationMappings(newOrig);

      queryClient.invalidateQueries(["atreemo-status"]);
      navigate("/activation-success", {
        state: {
          description: "Your customers will now sync with your selected Atreemo brands/locations.",
          integrationType: "atreemo",
        },
      });
      setToastMessage("Location mappings saved successfully");
    } catch (err) {
      console.error("Error saving location mappings:", err);
      setToastMessage(err?.message || "Failed to save location mappings");
    } finally {
      setIsLoading(false);
      setProgressValue(0);
    }
  };

  if (!location.state) {
    return <div>No authentication data found. Please go back and log your credentials again.</div>;
  }

  return (
    <Flex style={{ minHeight: "100vh" }} justify="center" align="center" p="6">
      <Card style={{ maxWidth: "1000px", width: "100%" }} p="4">
        <Flex style={{ alignSelf: "stretch" }} display="flex" direction="column" align="start">
          {/* Header */}
          <Flex style={{ alignSelf: "stretch" }} display="flex" justify="between" align="center">
            <Flex display="flex" align="center" gap="4">
              <Flex
                style={{
                  borderRadius: "var(--space-1)",
                  background: "#FFFFFF",
                  border: "1px solid var(--gray-a6)",
                }}
                height="40px"
                width="40px"
                p="2"
                justify="center"
                align="center"
              >
                <img src={require("../../../assets/images/atreemo.jpg")} alt="atreemo" style={{ width: "24px" }} />
              </Flex>
              <Flex direction="column" justify="center" align="start">
                <Text size="4" weight="medium">Atreemo</Text>
                <Text style={{ color: "var(--gray-a11)" }} size="1">
                  Map brands & locations (per-location mapping — group by bconnect brand)
                </Text>
              </Flex>
            </Flex>

            <Flex display="flex" align="center" gap="4">
              <Button size="2" variant="soft" onClick={() => navigate(-1)} color="gray">Cancel</Button>
              {/* <Button
                size="2"
                variant="soft"
                onClick={() => handleSaveLocationMappings("all")}
                loading={isLoading}
                disabled={!(hasBrandSectionChanges || hasIndependentLocationChanges) || isLoading}
              >
                Save and finish
              </Button> */}
              <Button
                size="2"
                variant="soft"
                onClick={() => handleSaveLocationMappings("all")}
                loading={isLoading}
                disabled={
                  isLoading ||
                  hasBrandHalfFilled ||
                  hasIndependentHalfFilled ||
                  !(hasBrandSectionChanges || hasIndependentLocationChanges)
                }
              >
                Save and finish
              </Button>
            </Flex>
          </Flex>

          <Separator orientation="horizontal" size="4" my="4" />

          {/* BRANDS section: group bconnect locations under each bconnect brand */}
          <Flex style={{ alignSelf: "stretch" }} display="flex" align="start" gap="5" mb="4">
            <Flex style={{ width: "296px" }} direction="column" align="start" gap="2">
              <Text size="2" weight="medium">Brands</Text>
              <Text size="1" weight="regular">Group: b connect brands with each of their locations (map each location independently).</Text>
            </Flex>

            <Flex direction="column" gap="3" style={{ width: "100%" }}>
              <Flex direction="row" justify="between" style={{ width: "100%" }}>
                <Text size="2" weight="medium" style={{ width: "32%" }}>b connect location</Text>
                <Text size="2" weight="medium" style={{ width: "32%" }}>Atreemo brand</Text>
                <Text size="2" weight="medium" style={{ width: "32%", paddingLeft: "18px" }}>Atreemo location</Text>
              </Flex>

              {(bconnectBrands || []).length > 0 ? (
                (bconnectBrands || []).map((b) => {
                  const brandLocations = brandLocationsMap[b.id] || [];
                  return (
                    <Flex key={b.id} direction="column" gap="2" style={{ width: "100%" }}>
                      <Flex direction="row" style={{ width: "100%", alignItems: "center", gap: 8 }}>
                        <Text size="2" weight="medium" style={{ width: "100%" }}>[{b.name}]</Text>
                      </Flex>

                      {(brandLocations || []).length > 0 ? (
                        brandLocations.map((loc) => {
                          const locId = loc.id ?? loc.value ?? loc.location_id;
                          const key = String(locId);
                          const mapping = locationMappings[key] || {};
                          const isMapped = !!mapping.isMapped;

                          return (
                            <Flex key={key} direction="row" justify="between" style={{ width: "100%" }}>
                              <Flex style={{ width: "32%", minWidth: 0 }}>
                                <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                                  <Text size="2" title={loc.name ?? loc.label ?? ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                                    {loc.name ?? loc.label ?? ""}
                                  </Text>
                                </Flex>
                              </Flex>

                              <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                                <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                                  <ArrowRightIcon width="16" height="16" />
                                </IconButton>
                              </Flex>

                              <Flex style={{ width: "32%", minWidth: 0 }}>
                                {isMapped && mapping.atreemo_brand_id ? (
                                  <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                                    <Text size="2" title={atreemoBrands.find((ab) => String(ab.id) === String(mapping.atreemo_brand_id))?.name || ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                                      {atreemoBrands.find((ab) => String(ab.id) === String(mapping.atreemo_brand_id))?.name || ""}
                                    </Text>
                                  </Flex>
                                ) : (
                                  <Select.Root value={mapping.atreemo_brand_id || ""} onValueChange={(v) => handleLocationAtreemoBrandChange(key, v)} style={{ width: "100%" }}>
                                    <Select.Trigger placeholder="Select..." style={{ width: "100%", boxSizing: "border-box" }}>
                                      <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.atreemo_brand_id ? (atreemoBrands.find((ab) => String(ab.id) === String(mapping.atreemo_brand_id))?.name || "") : "Select an Atreemo brand"}>
                                        {mapping.atreemo_brand_id ? (atreemoBrands.find((ab) => String(ab.id) === String(mapping.atreemo_brand_id))?.name || "") : "Select an Atreemo brand"}
                                      </div>
                                    </Select.Trigger>
                                    <Select.Content side="bottom" align="start" sideOffset={4}>
                                      {atreemoBrands.map((ab) => <Select.Item key={ab.id} value={ab.id} title={ab.name}>{ab.name}</Select.Item>)}
                                    </Select.Content>
                                  </Select.Root>
                                )}
                              </Flex>

                              <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                                <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                                  <ArrowRightIcon width="16" height="16" />
                                </IconButton>
                              </Flex>

                              <Flex style={{ width: "32%", minWidth: 0 }}>
                                {isMapped && mapping.atreemo_location_id ? (
                                  <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                                    <Text size="2" title={atreemoLocations.find((al) => String(al.id) === String(mapping.atreemo_location_id))?.name || ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                                      {atreemoLocations.find((al) => String(al.id) === String(mapping.atreemo_location_id))?.name || ""}
                                    </Text>
                                  </Flex>
                                ) : (
                                  <Select.Root value={mapping.atreemo_location_id || ""} onValueChange={(v) => handleLocationAtreemoLocationChange(key, v)} style={{ width: "100%" }}>
                                    <Select.Trigger placeholder="Select..." style={{ width: "100%", boxSizing: "border-box" }}>
                                      <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.atreemo_location_id ? (atreemoLocations.find((al) => String(al.id) === String(mapping.atreemo_location_id))?.name || "") : "Select an Atreemo location"}>
                                        {mapping.atreemo_location_id ? (atreemoLocations.find((al) => String(al.id) === String(mapping.atreemo_location_id))?.name || "") : "Select an Atreemo location"}
                                      </div>
                                    </Select.Trigger>
                                    <Select.Content side="bottom" align="start" sideOffset={4}>
                                      {atreemoLocations.map((al) => <Select.Item key={al.id} value={al.id} title={al.name}>{al.name}</Select.Item>)}
                                    </Select.Content>
                                  </Select.Root>
                                )}
                              </Flex>
                            </Flex>
                          );
                        })
                      ) : (
                        <Flex justify="center" style={{ padding: "8px 0 16px 0" }}>
                          <Text size="2" style={{ color: "var(--gray-a9)" }}>No locations for this brand</Text>
                        </Flex>
                      )}
                    </Flex>
                  );
                })
              ) : (
                <Flex justify="center" style={{ padding: "20px 0" }}>
                  <Text>No brands found.</Text>
                </Flex>
              )}

              {/* <Flex justify="end">
                <Button
                  size="2"
                  variant="solid"
                  color="blue"
                  onClick={() => handleSaveLocationMappings("brands")}
                  loading={isLoading}
                  disabled={!hasBrandSectionChanges || isLoading}
                >
                  Save changes
                </Button>
              </Flex> */}
            </Flex>
          </Flex>

          <Separator orientation="horizontal" size="4" my="2" />

          {/* LOCATIONS (independent / default-brand locations) */}
          <Flex style={{ alignSelf: "stretch" }} display="flex" align="start" gap="5">
            <Flex style={{ width: "296px" }} direction="column" align="start" gap="2">
              <Text size="2" weight="medium">Locations</Text>
              <Text size="1" weight="regular">Map independent b connect locations to Atreemo brands/locations.</Text>
            </Flex>

            <Flex direction="column" gap="3" style={{ width: "100%" }}>
              <Flex direction="row" justify="between" style={{ width: "100%" }}>
                <Text size="2" weight="medium" style={{ width: "32%" }}>b connect location</Text>
                <Text size="2" weight="medium" style={{ width: "32%" }}>Atreemo brand</Text>
                <Text size="2" weight="medium" style={{ width: "32%", paddingLeft: "18px" }}>Atreemo location</Text>
              </Flex>

              {(independentLocations || []).length > 0 ? (
                (independentLocations || []).map((loc) => {
                  const locId = loc.id ?? loc.value ?? loc.location_id;
                  const key = String(locId);
                  const mapping = locationMappings[key] || {};
                  const isMapped = !!mapping.isMapped;
                  return (
                    <Flex key={key} direction="row" justify="between" style={{ width: "100%" }}>
                      <Flex style={{ width: "32%", minWidth: 0 }}>
                        <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                          <Text size="2" title={loc.name ?? loc.label ?? ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                            {loc.name ?? loc.label ?? ""}
                          </Text>
                        </Flex>
                      </Flex>

                      <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                        <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                          <ArrowRightIcon width="16" height="16" />
                        </IconButton>
                      </Flex>

                      <Flex style={{ width: "32%", minWidth: 0 }}>
                        {isMapped && mapping.atreemo_brand_id ? (
                          <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                            <Text size="2" title={atreemoBrands.find((ab) => String(ab.id) === String(mapping.atreemo_brand_id))?.name || ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                              {atreemoBrands.find((ab) => String(ab.id) === String(mapping.atreemo_brand_id))?.name || ""}
                            </Text>
                          </Flex>
                        ) : (
                          <Select.Root value={mapping.atreemo_brand_id || ""} onValueChange={(v) => handleLocationAtreemoBrandChange(key, v)} style={{ width: "100%" }}>
                            <Select.Trigger placeholder="Select..." style={{ width: "100%", boxSizing: "border-box" }}>
                              <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.atreemo_brand_id ? (atreemoBrands.find((ab) => String(ab.id) === String(mapping.atreemo_brand_id))?.name || "") : "Select an Atreemo brand"}>
                                {mapping.atreemo_brand_id ? (atreemoBrands.find((ab) => String(ab.id) === String(mapping.atreemo_brand_id))?.name || "") : "Select an Atreemo brand"}
                              </div>
                            </Select.Trigger>
                            <Select.Content side="bottom" align="start" sideOffset={4}>
                              {atreemoBrands.map((ab) => <Select.Item key={ab.id} value={ab.id} title={ab.name}>{ab.name}</Select.Item>)}
                            </Select.Content>
                          </Select.Root>
                        )}
                      </Flex>

                      <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                        <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                          <ArrowRightIcon width="16" height="16" />
                        </IconButton>
                      </Flex>

                      <Flex style={{ width: "32%", minWidth: 0 }}>
                        {isMapped && mapping.atreemo_location_id ? (
                          <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                            <Text size="2" title={atreemoLocations.find((al) => String(al.id) === String(mapping.atreemo_location_id))?.name || ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                              {atreemoLocations.find((al) => String(al.id) === String(mapping.atreemo_location_id))?.name || ""}
                            </Text>
                          </Flex>
                        ) : (
                          <Select.Root value={mapping.atreemo_location_id || ""} onValueChange={(v) => handleLocationAtreemoLocationChange(key, v)} style={{ width: "100%" }}>
                            <Select.Trigger placeholder="Select..." style={{ width: "100%", boxSizing: "border-box" }}>
                              <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.atreemo_location_id ? (atreemoLocations.find((al) => String(al.id) === String(mapping.atreemo_location_id))?.name || "") : "Select an Atreemo location"}>
                                {mapping.atreemo_location_id ? (atreemoLocations.find((al) => String(al.id) === String(mapping.atreemo_location_id))?.name || "") : "Select an Atreemo location"}
                              </div>
                            </Select.Trigger>
                            <Select.Content side="bottom" align="start" sideOffset={4}>
                              {atreemoLocations.map((al) => <Select.Item key={al.id} value={al.id} title={al.name}>{al.name}</Select.Item>)}
                            </Select.Content>
                          </Select.Root>
                        )}
                      </Flex>
                    </Flex>
                  );
                })
              ) : (
                <Flex justify="center" style={{ padding: "20px 0" }}>
                  <Text>No locations found.</Text>
                </Flex>
              )}

              {/* <Flex justify="end">
                <Button
                  size="2"
                  variant="solid"
                  color="blue"
                  onClick={() => handleSaveLocationMappings("independent")}
                  loading={isLoading}
                  disabled={!hasIndependentLocationChanges || isLoading}
                >
                  Save changes
                </Button>
              </Flex> */}
            </Flex>
          </Flex>
        </Flex>
      </Card>

      <div style={{ position: "fixed", zIndex: 2147483647, pointerEvents: "none" }}>
        <ToastAlert />
      </div>
    </Flex>
  );
}